import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { API_ENDPOINTS, buildTenantApiUrl } from '../config/api.config';
import {
  AuthResponse,
  LoginOtpRequest,
  RegisterRequest,
  VerifyOtpRequest,
} from '../models/auth.model';
import { IndexedDbCacheService } from './indexed-db-cache.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenKey = 'day-pass.access-token';
  private readonly profileKey = 'day-pass.user-profile';
  private readonly cache = inject(IndexedDbCacheService);
  readonly user = signal<AuthResponse | null>(this.loadUser());

  sendSignupOtp(payload: LoginOtpRequest): Observable<string> {
    // The OTP endpoint may return an empty or plain-text success body. Using
    // responseType text prevents a successful OTP request from becoming a
    // JSON parsing error in Angular.
    return this.http.post(buildTenantApiUrl(API_ENDPOINTS.auth.sendSignupOtp), payload, {
      responseType: 'text',
    });
  }

  sendLoginOtp(payload: LoginOtpRequest): Observable<string> {
    return this.http.post(buildTenantApiUrl(API_ENDPOINTS.auth.sendLoginOtp), payload, {
      responseType: 'text',
    });
  }

  verifySignupOtp(payload: VerifyOtpRequest): Observable<unknown> {
    return this.http.post<AuthResponse>(
      buildTenantApiUrl(API_ENDPOINTS.auth.verifySignupOtp),
      payload,
    );
  }

  verifyLoginOtp(payload: VerifyOtpRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      buildTenantApiUrl(API_ENDPOINTS.auth.verifyLoginOtp),
      payload,
    );
  }

  register(payload: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(buildTenantApiUrl(API_ENDPOINTS.auth.register), payload);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getEmployeeId(): string | null {
    const user = this.user();
    return user?.employee_id ?? user?.employeeId ?? user?.id ?? null;
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.profileKey);
    this.user.set(null);
    void this.cache.clear();
  }

  saveTokenFromResponse(response: AuthResponse): void {
    const token = response.accessToken ?? response.access_token ?? response.token;
    if (token) {
      this.setToken(token);
      void this.cache.clear();
    }
    if (
      response.id ||
      response.employee_id ||
      response.employeeId ||
      response.name ||
      response.email ||
      response.phone ||
      response.username
    ) {
      this.user.set(response);
      localStorage.setItem(this.profileKey, JSON.stringify(response));
    }
  }

  private loadUser(): AuthResponse | null {
    try {
      const storedUser = localStorage.getItem(this.profileKey);
      return storedUser ? (JSON.parse(storedUser) as AuthResponse) : null;
    } catch {
      return null;
    }
  }
}
